import { createReleaseMaintenanceView, readReleaseStatus } from './release_maintenance_view.mjs';
import { setReleaseClientId, releaseRequestsPending } from './release_client_state.js';
import { S } from './app_state.js?v=20260817c';
import { flushForMaintenance } from '../project/project_persistence_service.js?v=20260904f';
import { createCleanWindowRetirement } from './release_clean_window_retirement.mjs';

let started = false;
let locked = false;
let guardInstalled = false;
const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'touchstart', 'touchmove', 'touchend', 'keydown', 'keyup', 'beforeinput', 'input', 'change', 'click', 'dblclick', 'paste', 'cut', 'drop', 'wheel'];
const block = event => {
  if (!locked) return;
  event.preventDefault(); event.stopImmediatePropagation();
};
export function installReleaseMaintenanceInputGuard() {
  if (guardInstalled) return;
  guardInstalled = true;
  for (const type of events) window.addEventListener(type, block, { capture: true, passive: false });
}

// One registration per window. The runner can time out missing ACKs, but this
// document retains unsaved data and never falsely reports a successful save.
export async function startReleaseMaintenance() {
  if (started) return;
  started = true;
  installReleaseMaintenanceInputGuard();
  const originalFetch = window.fetch.bind(window);
  const id = crypto.randomUUID();
  let retirementStorage;
  try { retirementStorage = window.sessionStorage; } catch { /* Restricted browser storage. */ }
  const cleanWindows = createCleanWindowRetirement(retirementStorage);
  let knownRun = null;
  let ready = false;
  setReleaseClientId(id);
  let stopped = false;
  let announced = false;
  let frozenDrafts = {};
  const unsaved = () => Boolean(S.file?.dirty) || Object.keys(S.project?.inputDrafts || {}).length > 0 || Object.keys(frozenDrafts).length > 0;
  const flush = async () => {
    await flushForMaintenance({ inputDrafts: frozenDrafts });
    frozenDrafts = {};
  };
  let currentState = { phase: 'idle', run: null };
  const view = createReleaseMaintenanceView({ onDeadline: () => {
    currentState = { ...currentState, phase: 'drain' };
    show('', true);
  } });
  function show(text = '', freeze = false, error = false) {
    if (freeze && !locked) {
      document.activeElement?.blur();
      window.dispatchEvent(new Event('blur'));
      frozenDrafts = { ...(S.project?.inputDrafts || {}) };
      locked = true;
    }
    view.update(currentState, { message: text, error });
  }
  const dirty = () => { ready = false; };
  window.addEventListener('ks:project-dirty', dirty);
  async function report(extra = {}) {
    const response = await originalFetch('/api/release/client', {
      method: 'POST', signal: AbortSignal.timeout(10000), headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, run: knownRun, ready, dirty: unsaved(), ...extra })
    });
    if (!response.ok) throw new Error('Speicherbestätigung konnte nicht übermittelt werden.');
    return response.json();
  }
  const closeCleanWindow = event => {
    if (!unsaved() && !releaseRequestsPending()) {
      // A bfcache document may return with its original ID; it must not leave
      // a delayed close that a duplicated tab could apply to that live page.
      if (event?.persisted !== true) cleanWindows.remember(id);
      try {
        navigator.sendBeacon('/api/release/client', new Blob([JSON.stringify({ id, close: true, dirty: false })], { type: 'application/json' }));
      } catch { /* Retry the recorded clean close after navigation. */ }
    }
  };
  window.addEventListener('pagehide', closeCleanWindow);
  async function tick() {
    try {
      const state = await readReleaseStatus(originalFetch, Date.now(), { active: ['announce', 'drain', 'maintenance'].includes(currentState.phase) });
      if (state.phase === 'announce' && state.deadline <= Date.now()) state.phase = 'drain';
      currentState = state;
      if (state.phase === 'disabled') {
        stopped = true; locked = false; guardInstalled = false; setReleaseClientId(null);
        window.removeEventListener('pagehide', closeCleanWindow);
        window.removeEventListener('ks:project-dirty', dirty);
        for (const type of events) window.removeEventListener(type, block, true);
        view.destroy(); return;
      }
      knownRun = state.run;
      if (state.phase === 'idle') {
        if (locked) {
          // Never reload unsaved data, including asynchronous edits after ACK.
          await flush();
          if (!unsaved() && !releaseRequestsPending()) { await report({ close: true, dirty: false }); location.reload(); }
        } else {
          await report();
        }
      } else if (state.phase === 'cancelled') {
        if (locked) {
          // Cancellation before mutation permits fixing a save conflict without
          // losing the local document or being trapped behind the overlay.
          if (Object.keys(frozenDrafts).length) {
            S.project.inputDrafts = { ...frozenDrafts, ...(S.project.inputDrafts || {}) };
            S.file.dirty = true;
          }
          frozenDrafts = {};
          locked = false;
        }
        ready = false;
        if (announced) show();
        await report();
      } else if (state.phase === 'announce') {
        announced = true;
        show();
        await report();
        // Start saving during the full announcement window, not only when
        // editing is frozen. Drain flushes any edits made afterwards again.
        if (unsaved() && !releaseRequestsPending()) await flush();
      } else if (state.phase === 'drain') {
        announced = true;
        show('', true);
        if (ready && !unsaved() && !releaseRequestsPending()) {
          await report();
        } else if (!releaseRequestsPending()) {
          ready = false;
          await report();
          await flush();
          // Allow queued model callbacks to settle, then verify once more.
          await new Promise(resolve => { setTimeout(resolve, 500); });
          if (unsaved() || releaseRequestsPending()) throw new Error('Änderungen werden noch abgeschlossen.');
          ready = true;
          await report();
          show('', true);
        }
      } else {
        show('', true);
        if (unsaved()) { ready = false; await report({ error: 'Ungespeicherte Zeichnung wartet auf Verbindung.' }); }
      }
      // Register/save/freeze this document first. Only then retire the known
      // clean documents that actually left this tab. Server ownership checks
      // still apply; failed requests stay pending, without expiring any lease.
      await cleanWindows.retire(id, async oldId => (await originalFetch('/api/release/client', {
        method: 'POST', signal: AbortSignal.timeout(10000), headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: oldId, close: true, dirty: false })
      })).ok);
    } catch (error) {
      ready = false;
      // Connectivity/registration errors are not evidence of an update.
      // Once a real update locks the window, retain its save protection.
      if (locked) {
        if (!['drain', 'maintenance'].includes(currentState.phase)) currentState = { ...currentState, phase: 'maintenance' };
        show(unsaved() ? 'Ihre Änderungen konnten noch nicht gespeichert werden. Bitte lassen Sie dieses Fenster geöffnet.' : '', true, unsaved());
      }
      if (announced) await report({ ready: false, error: error.message }).catch(() => { /* Retry when the server is reachable. */ });
    }
    if (!stopped) window.setTimeout(tick, 2000);
  }
  await tick();
}
